/**
 * The livro as a thread — the rules, shared by both web surfaces.
 *
 * A page of the livro develops: "falei com o morador", "técnico vem quinta", "resolvido". The apps
 * have shown that for a while — the state of each page, who closed it and what they wrote, every
 * comment in between. The web showed none of it: `LivroPanel` (which backs BOTH the Operational
 * View panel and the /livro page) rendered content, author and timestamp and stopped. The same
 * record therefore read as finished in the app and as open on the web, and a conclusion written on
 * a phone was invisible to whoever was at the desk.
 *
 * These rules are a mirror of `LivroThread.kt` and `LivroThread.swift`, and the server enforces
 * the same (services/livro_policy.go). They live in a module rather than inside the component so
 * the three platforms can be read side by side, and so the limits cannot drift apart silently.
 * See livroThread.test.ts.
 */

import type { CustomerNote } from '../../../hooks/useCustomerNotes';
import { hasAnyRole, type RoleName } from '../../../config/roles';

export const LIVRO_KIND_COMMENT = 'comment';
export const LIVRO_KIND_CONCLUDED = 'concluded';
export const LIVRO_KIND_REOPENED = 'reopened';

/** One follow-up on a page. Append-only on the server: no edit, no delete. */
export interface LivroNoteEntry {
  id: string;
  kind?: string;
  body: string;
  author_name?: string;
  created_at: string;
}

/**
 * How much has to be written before an entry can be saved.
 *
 * A livro page is read by the next shift, and by whoever asks about it six months later — so "ok"
 * is not an entry. Five is a low bar on purpose: it stops the accidental "." and the stray
 * keystroke without turning a handover into an essay. The ceiling is there because this is a log
 * line, not a report.
 *
 * Distinct from `LIVRO_MIN_CHARS`/`LIVRO_MAX_CHARS` in LivroPanel, which bound a whole new
 * OCCURRENCE; these bound a follow-up on an existing one.
 */
export const LIVRO_ENTRY_MIN_CHARS = 5;
export const LIVRO_ENTRY_MAX_CHARS = 240;

/** Which pages of the book the screen is showing. */
export type LivroFilter = 'todas' | 'abertas' | 'concluidas';

export const LIVRO_FILTERS: LivroFilter[] = ['todas', 'abertas', 'concluidas'];

/**
 * The whole book, by default.
 *
 * "What is still pending" is the handover question and "what happened in March" is the history
 * question; both are real, and the book is the one that was missing.
 */
export const DEFAULT_LIVRO_FILTER: LivroFilter = 'todas';

export const statusLabel = (note: CustomerNote): string =>
  note.is_active ? 'Em aberto' : 'Concluída';

/**
 * Commenting survives closing on purpose: "o vazamento voltou" is worth recording before anybody
 * decides whether to reopen the page.
 */
export const canComment = (_note: CustomerNote): boolean => true;

export const canConclude = (note: CustomerNote): boolean => note.is_active;

export const canReopen = (note: CustomerNote): boolean => !note.is_active;

/**
 * A closure must say what happened: the next reader cannot ask. Short is fine — "morador retirou"
 * is a complete answer; the rule is that there IS one, and that it is long enough to mean
 * something.
 */
export const canSubmitEntry = (text: string): boolean => {
  const length = text.trim().length;
  return length >= LIVRO_ENTRY_MIN_CHARS && length <= LIVRO_ENTRY_MAX_CHARS;
};

/**
 * Characters typed against the limit, e.g. "15/240". Counts what is IN the field rather than the
 * trimmed value: a counter that disagrees with the characters on screen reads as a bug.
 */
export const counter = (text: string): string => `${text.length}/${LIVRO_ENTRY_MAX_CHARS}`;

/**
 * What a blocked closure is missing, in the words of what is being done.
 *
 * The commit control is unavailable until the entry is long enough, which is only acceptable while
 * the screen states the reason — an unavailable control with no stated reason is a dead button,
 * which is exactly how this was reported on mobile.
 */
export const closureRequirement = (note: CustomerNote): string =>
  note.is_active
    ? 'Para concluir, conte o que aconteceu.'
    : 'Para reabrir, diga por que está voltando.';

/**
 * What the screen is waiting for, or null once it is waiting for nothing. The ladder is
 * deliberate: an empty field is told what it is FOR, a field that has been started is told the
 * RULE.
 */
export const writingHint = (note: CustomerNote, text: string): string | null => {
  const trimmed = text.trim();
  if (trimmed.length === 0) return closureRequirement(note);
  if (trimmed.length < LIVRO_ENTRY_MIN_CHARS) return `Escreva ao menos ${LIVRO_ENTRY_MIN_CHARS} caracteres.`;
  if (trimmed.length > LIVRO_ENTRY_MAX_CHARS) return `Máximo de ${LIVRO_ENTRY_MAX_CHARS} caracteres.`;
  return null;
};

/** Oldest first: a thread is a chronology, not a feed you are catching up with. */
export const orderedEntries = <T extends { created_at: string }>(entries: T[]): T[] =>
  [...entries].sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));

/**
 * An unknown kind reads as a plain comment rather than as a blank row — the words are still there
 * and still worth showing.
 */
export const kindLabel = (kind?: string): string => {
  switch ((kind ?? '').trim().toLowerCase()) {
    case LIVRO_KIND_CONCLUDED: return 'Concluída';
    case LIVRO_KIND_REOPENED: return 'Reaberta';
    default: return 'Comentário';
  }
};

export const matching = (notes: CustomerNote[], filter: LivroFilter): CustomerNote[] => {
  switch (filter) {
    case 'abertas': return notes.filter(n => n.is_active);
    case 'concluidas': return notes.filter(n => !n.is_active);
    default: return notes;
  }
};

export const filterLabel = (filter: LivroFilter): string => {
  switch (filter) {
    case 'abertas': return 'Em aberto';
    case 'concluidas': return 'Concluídas';
    default: return 'Todas';
  }
};

/**
 * What to call a page: the note's own id, shortened to something a person can read out ("a página
 * A3F19C"). Deliberately NOT a position in the list — a position renumbers every time anything
 * above it is removed, and then two people discuss different pages by the same name.
 */
export const pageLabel = (noteId: string): string => {
  const reference = noteId.replace(/-/g, '').trim().slice(0, 6).toUpperCase();
  return reference ? `Página #${reference}` : 'Página';
};

/**
 * Who may CLOSE a page, as opposed to write one.
 *
 * The portaria writes the book: a porteiro records what happened and adds to it all shift.
 * Deciding that an occurrence is FINISHED is the síndico's call, because the closure is what the
 * condomínio gets answered with later. Mirrors LivroThread.canCloseLivroPage on both apps.
 *
 * `administrador` is deliberately NOT here: it sits at staff level in ROLE_HIERARCHY and is a
 * monitoring role, not the one that answers for the book. The server is more permissive than all
 * of this (the conclude route is auth-only), so this is a product rule the screens hold, not a
 * security boundary.
 */
export const canManageLivro = (userRoles: string[]): boolean => hasAnyRole(userRoles, [
  'sindico', 'supervisor', 'condo_manager', 'admin', 'condo_admin', 'super_admin', 'condo_super_admin',
] as RoleName[]);

export const canCloseLivroPage = (note: CustomerNote, isManager: boolean): boolean =>
  isManager && canConclude(note);

export const canReopenLivroPage = (note: CustomerNote, isManager: boolean): boolean =>
  isManager && canReopen(note);

